import React from 'react';
import {
  ArrowRight,
  BookOpen,
  TrendingUp,
  Users,
  Clock,
  Star,
  CheckCircle,
  Award
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const InsightsPage = () => {
  const { t } = useLanguage();

  const caseStudies = Array.isArray(t('insightsPage.caseStudies'))
    ? t('insightsPage.caseStudies')
    : [];

  const summaryStats = Array.isArray(t('insightsPage.summary.stats'))
    ? t('insightsPage.summary.stats')
    : [];

  const yourStoryTiles = Array.isArray(t('insightsPage.yourStory.tiles'))
    ? t('insightsPage.yourStory.tiles')
    : [];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - gleich wie TRIAX */}
      <section className="relative py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-r from-purple-600/30 to-orange-500/30 rounded-full blur-3xl animate-float" />
          <div
            className="absolute bottom-1/4 -right-20 w-80 h-80 bg-gradient-to-r from-orange-500/30 to-purple-600/30 rounded-full blur-3xl animate-float"
            style={{ animationDelay: '-3s' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600/10 to-orange-500/10 rounded-full border border-purple-200 mb-8">
              <BookOpen className="w-4 h-4 text-purple-600 mr-2" />
              <span className="text-sm font-medium text-slate-700">
                {t('insightsPage.hero.chipLabel')}
              </span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-light text-white mb-8">
              <span className="gradient-text font-medium">
                {t('insightsPage.hero.titleHighlight')}
              </span>
              <br />
              {t('insightsPage.hero.titleRest')}
            </h1>

            <p className="text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed font-light mb-12">
              {t('insightsPage.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-center space-x-8">
            <button className="px-6 py-3 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-medium">
              {t('insightsPage.tabs.successStories')}
            </button>
            <button className="px-6 py-3 text-slate-600 hover:text-purple-600 transition-colors font-medium">
              {t('insightsPage.tabs.caseStudies')}
            </button>
            <button className="px-6 py-3 text-slate-600 hover:text-purple-600 transition-colors font-medium">
              {t('insightsPage.tabs.blogPlanned')}
            </button>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-20">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className="grid lg:grid-cols-2 gap-16 items-center"
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="mb-6">
                    <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
                      {study.category}
                    </span>
                    <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
                      <span className="gradient-text font-medium">
                        {study.title}
                      </span>
                    </h2>
                  </div>

                  {/* Challenge */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium text-slate-900 mb-3 flex items-center">
                      <TrendingUp className="w-5 h-5 text-red-500 mr-2" />
                      {t('insightsPage.labels.challenge')}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {study.challenge}
                    </p>
                  </div>

                  {/* Solution */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium text-slate-900 mb-3 flex items-center">
                      <CheckCircle className="w-5 h-5 text-blue-500 mr-2" />
                      {t('insightsPage.labels.solution')}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {study.solution}
                    </p>
                  </div>

                  {/* Results */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
                      <Award className="w-5 h-5 text-green-500 mr-2" />
                      {t('insightsPage.labels.results')}
                    </h3>
                    <ul className="space-y-2">
                      {study.results.map((result, idx) => (
                        <li key={idx} className="flex items-start">
                          <Star className="w-4 h-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                          <span className="text-slate-700 font-medium">
                            {result}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Quote */}
                  <blockquote className="bg-gradient-to-r from-purple-50 to-orange-50 p-6 rounded-2xl border border-purple-100 mb-8">
                    <p className="text-slate-700 italic leading-relaxed mb-4">
                      {study.quote}
                    </p>
                    <cite className="text-sm text-purple-600 font-medium">
                      – {study.author}
                    </cite>
                  </blockquote>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <Clock className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <div className="text-sm font-medium text-slate-900">
                        {study.metrics.timeline}
                      </div>
                      <div className="text-xs text-slate-600">
                        {t('insightsPage.metrics.timeline')}
                      </div>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
                      <div className="text-sm font-medium text-slate-900">
                        {study.metrics.roi}
                      </div>
                      <div className="text-xs text-slate-600">
                        {t('insightsPage.metrics.roi')}
                      </div>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <Users className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                      <div className="text-sm font-medium text-slate-900">
                        {study.metrics.satisfaction}
                      </div>
                      <div className="text-xs text-slate-600">
                        {t('insightsPage.metrics.satisfaction')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative group">
                    <img
                      src={study.image}
                      alt={study.title}
                      className="rounded-3xl shadow-2xl transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-orange-500/20 rounded-3xl" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Summary Stats */}
      <section className="py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl lg:text-4xl font-light text-white mb-8">
              {t('insightsPage.summary.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('insightsPage.summary.highlight')}
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {summaryStats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-8 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/30"
              >
                <div className="text-4xl font-bold gradient-text mb-2">
                  {stat.metric}
                </div>
                <div className="text-slate-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Your Story CTA */}
      <section className="py-32 bg-gradient-to-r from-purple-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-8">
            {t('insightsPage.yourStory.titleBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('insightsPage.yourStory.highlight')}
            </span>
            ?
          </h2>

          <p className="text-xl text-slate-600 leading-relaxed font-light mb-12">
            {t('insightsPage.yourStory.text')}
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {yourStoryTiles.map((tile, idx) => {
              const Icon =
                idx === 0 ? CheckCircle : idx === 1 ? TrendingUp : Award;
              const iconColor =
                idx === 0
                  ? 'text-green-500'
                  : idx === 1
                  ? 'text-purple-600'
                  : 'text-orange-600';

              return (
                <div
                  key={idx}
                  className="p-6 bg-white rounded-2xl card-premium"
                >
                  <Icon
                    className={`w-12 h-12 mx-auto mb-4 ${iconColor}`}
                  />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    {tile.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{tile.text}</p>
                </div>
              );
            })}
          </div>

          <button className="inline-flex items-center px-12 py-6 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-medium text-xl transition-all duration-300 hover:shadow-glow hover:scale-105">
            <BookOpen className="w-5 h-5 mr-3" />
            <span className="mr-3">
              {t('insightsPage.yourStory.buttonLabel')}
            </span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
};
